// Fotos de banco livre (Unsplash License: uso comercial sem atribuição
// obrigatória; creditamos mesmo assim em public/images/stock/CREDITS.md).
// Nunca usar foto "Unsplash+" — é paga. Linha visual: pessoas e pequenos
// negócios reais, sem placa/texto e sem marca de terceiro em destaque.
// Os arquivos são servidos daqui (public/images/stock/), não da CDN:
//   bun run photos:fetch        baixa só o que falta
//   bun run photos:fetch --force  rebaixa tudo
// Regras de uso: docs/specs/design-system/tokens.md → "Fotografia".

export interface StockPhoto {
  /** Descrição para `alt` quando a foto é informativa. */
  alt: string;
  /** URL base da imagem na CDN do Unsplash (sem query). */
  imageUrl: string;
  pageUrl: string;
  author: string;
  authorUsername: string;
  /** `object-position` quando o centro corta o assunto (ex.: "50% 30%"). */
  position?: string;
  /** Qualidade WebP no download (padrão 72); baixar quando passar de ~250 KB. */
  quality?: number;
}

export const STOCK_PHOTO_WIDTHS = [800, 1600] as const;
/** Proporção fixa do recorte baixado (3:2). */
export const STOCK_PHOTO_RATIO = 2 / 3;

export const stockPhotos = {
  "vendas-balcao": {
    alt: "Vendedora entrega a sacola a uma cliente no balcão da loja",
    imageUrl: "https://images.unsplash.com/photo-1753161028463-62a316670f2d",
    pageUrl: "https://unsplash.com/photos/ku3ywiHnkIU",
    author: "Vitaly Gariev",
    authorUsername: "silverkblack",
  },
  "conversa-vendas": {
    alt: "Dois homens conversam sobre uma venda sentados à mesa",
    imageUrl: "https://images.unsplash.com/photo-1507537362848-9c7e70b7b5c1",
    pageUrl: "https://unsplash.com/photos/W3Jl3jREpDY",
    author: "LinkedIn Sales Solutions",
    authorUsername: "linkedinsalesnavigator",
  },
  "atendente-headset": {
    alt: "Atendente sorri com headset em frente ao computador",
    imageUrl: "https://images.unsplash.com/photo-1766066014237-00645c74e9c6",
    pageUrl: "https://unsplash.com/photos/wnpf3Q5pkXA",
    author: "BaljkanN4",
    authorUsername: "baljkann4",
  },
  "pedido-celular": {
    alt: "Empreendedora confere um pedido no celular ao lado das caixas prontas para envio",
    imageUrl: "https://images.unsplash.com/photo-1770013413878-2530e2c3d82b",
    pageUrl: "https://unsplash.com/photos/k63Or81F8-M",
    author: "Rifki Kurniawan",
    authorUsername: "kurniawann",
  },
  "dono-sobrecarregado": {
    alt: "Empresário com as mãos na cabeça diante do notebook, sobrecarregado",
    imageUrl: "https://images.unsplash.com/photo-1758876019043-cedb0b6f56ea",
    pageUrl: "https://unsplash.com/photos/hjh8cCOUtoo",
    author: "Vitaly Gariev",
    authorUsername: "silverkblack",
    position: "50% 35%",
  },
  "equipe-tranquila": {
    alt: "Equipe sorri reunida em volta de um notebook",
    imageUrl: "https://images.unsplash.com/photo-1572021335469-31706a17aaef",
    pageUrl: "https://unsplash.com/photos/Of_m3hMsoAA",
    author: "Jud Mackrill",
    authorUsername: "judmackrill",
    quality: 55,
  },
  "dona-cafeteria": {
    alt: "Dona de cafeteria sorri no salão do próprio negócio",
    imageUrl: "https://images.unsplash.com/photo-1753351052363-53ce102830eb",
    pageUrl: "https://unsplash.com/photos/QGg-vsPYbu8",
    author: "Vitaly Gariev",
    authorUsername: "silverkblack",
  },
  "equipe-balcao": {
    alt: "Dois funcionários de avental sorriem atrás do balcão da loja",
    imageUrl: "https://images.unsplash.com/photo-1753351054581-783229e920fa",
    pageUrl: "https://unsplash.com/photos/uRnRa8XXtrI",
    author: "Vitaly Gariev",
    authorUsername: "silverkblack",
  },
  "caixa-loja": {
    alt: "Comerciante sorri enquanto opera o caixa da loja",
    imageUrl: "https://images.unsplash.com/photo-1778791597308-a45982507d6b",
    pageUrl: "https://unsplash.com/photos/fgnYjaLsWDk",
    author: "SpotOn",
    authorUsername: "spotonpos",
  },
  "loja-online": {
    alt: "Casal de empreendedores acompanha as vendas da loja online no notebook",
    imageUrl: "https://images.unsplash.com/photo-1725429522610-9925b5cca06a",
    pageUrl: "https://unsplash.com/photos/gdjD2qm779Q",
    author: "Rifki Kurniawan",
    authorUsername: "kurniawann",
  },
  "pedido-fechado": {
    alt: "Empreendedora comemora ao telefone ao lado do notebook e das encomendas",
    imageUrl: "https://images.unsplash.com/photo-1700190827495-f9d8eec1a08c",
    pageUrl: "https://unsplash.com/photos/eMywyVm6hmw",
    author: "Rifki Kurniawan",
    authorUsername: "kurniawann",
  },
  "reuniao-equipe": {
    alt: "Equipe analisa junto um processo na tela do computador",
    imageUrl: "https://images.unsplash.com/photo-1758873268745-dd2cf0d677b5",
    pageUrl: "https://unsplash.com/photos/UikYLDQj9_I",
    author: "Vitaly Gariev",
    authorUsername: "silverkblack",
  },
  "aperto-de-mao": {
    alt: "Dois empresários apertam as mãos numa reunião",
    imageUrl: "https://images.unsplash.com/photo-1758519288905-38b7b00c1023",
    pageUrl: "https://unsplash.com/photos/lZXX-QVdyXY",
    author: "Vitaly Gariev",
    authorUsername: "silverkblack",
  },
  "mesa-trabalho": {
    alt: "Mesa de trabalho vista de cima com notebook, caderno e café",
    imageUrl: "https://images.unsplash.com/photo-1661956600684-97d3a4320e45",
    pageUrl: "https://unsplash.com/photos/04X1Yp9hNH8",
    author: "Mailchimp",
    authorUsername: "mailchimp",
  },
  "notebook-mesa": {
    alt: "Mulher trabalha no notebook numa mesa clara",
    imageUrl: "https://images.unsplash.com/photo-1743865319071-929ac8a27bcd",
    pageUrl: "https://unsplash.com/photos/WzC1lcwZCsY",
    author: "Aleksei Zhivilov",
    authorUsername: "bb009x",
  },
} satisfies Record<string, StockPhoto>;

export type StockPhotoId = keyof typeof stockPhotos;

export const stockPhotoSrc = (id: StockPhotoId, width: (typeof STOCK_PHOTO_WIDTHS)[number]) =>
  `/images/stock/${id}-${width}.webp`;
